import React, { useEffect, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import HomeGadgets from '../home/HomeGadgets';
import { workspaceGadgetsModel } from '../../utils/workspaceGadgets';
import { eventLine } from '../../utils/collab3Model';
import { listBrainFiles } from '../../services/WorkspacesService';
import {
  attentionItems, blockedTotal, buildRows, lastChanges, mergeActivity, projectRunState,
} from '../../utils/workspacePageModel';
import { ColumnsMenu, ProjectFilters, useHiddenColumns, useProjectListView } from './ProjectListTools';
import DataTable from '../table/DataTable';
import { eventTime, memberName, roleLabel } from '../../utils/workspaceDisplay';
const LATEST = 6;
const GADGET_COLUMNS = [
  { key: 'name', label: 'Project' },
  { key: 'state', label: 'State' },
  { key: 'quality', label: 'Quality' },
  { key: 'owner', label: 'Owner' },
];

/**
 * The Overview's Projects gadget: the workspace's projects with the Projects tab's tools (ProjectListTools.jsx): search,
 * State, Owner (Me, Others, a person), Has problems, sortable columns and Columns ▾.
 */
function ProjectsGadget({ projects, runStates, quality, activity, userSub, onOpenProject }) {
  const changes = useMemo(() => lastChanges(Object.fromEntries(Object.entries(activity).map(([id, entry]) => [id, entry?.value]))), [activity]);
  const all = useMemo(() => buildRows(projects, { answers: runStates, quality, changes, userSub }), [projects, runStates, quality, changes, userSub]);
  const { filters, setFilter, sort, sortBy, rows } = useProjectListView(all);
  const { show, toggle } = useHiddenColumns('flow.workspaceOverview.hiddenColumns', GADGET_COLUMNS);
  return (
    <section className="ws-card" data-testid="ws-gadget-projects">
      <h2 className="ws-card-title">Projects</h2>
      {projects.length === 0 ? <p className="ws-muted">No projects in this workspace yet. Add one from the Projects tab.</p> : (
        <>
          <div className="ws-tools ws-gadget-tools">
            <ProjectFilters all={all} filters={filters} label="projects in the gadget" setFilter={setFilter} />
            <ColumnsMenu columns={GADGET_COLUMNS} show={show} toggle={toggle} />
          </div>
          <DataTable
            actions={{
              label: 'Open',
              width: 84,
              render: (row) => <button aria-label={`Open ${row.name}`} className="ws-btn" onClick={() => onOpenProject(row.projectId)} type="button">Open</button>,
            }}
            ariaLabel="Projects"
            className="ws-table is-compact"
            columnState={{ show, toggle }}
            columns={[
              {
                key: 'name',
                label: 'Project',
                value: (row) => row.name,
                title: (row) => row.name,
                render: (row) => (
                  <>
                    <span aria-hidden="true" className={`ws-dot is-${row.state.tone}`} />{' '}
                    {/* The name opens the project, as on the Projects tab. */}
                    <button className="ws-name" data-testid="ws-gadget-project-name" onClick={() => onOpenProject(row.projectId)} type="button"><strong>{row.name}</strong></button>
                  </>
                ),
              },
              { key: 'state', label: 'State', width: 120, value: (row) => row.state.label, render: (row) => <span className={`ws-chip is-${row.state.tone}`}>{row.state.label}</span> },
              { key: 'quality', label: 'Quality', width: 150, value: (row) => row.quality.text, render: (row) => <span className={`ws-chip is-${row.quality.tone}`}>{row.quality.text}</span> },
              { key: 'owner', label: 'Owner', width: 150, className: 'ws-muted ws-owner', value: (row) => row.owner },
            ]}
            empty={<span className="ws-muted">No project matches.</span>}
            onSort={(next) => sortBy(next.key)}
            rowKey="projectId"
            rowProps={() => ({ className: 'ws-proj-line' })}
            rows={rows}
            scrollLabel="Projects, scrolls sideways"
            serverSort
            sort={sort}
          />
        </>
      )}
    </section>
  );
}

ProjectsGadget.propTypes = {
  activity: PropTypes.object.isRequired,
  onOpenProject: PropTypes.func.isRequired,
  projects: PropTypes.array.isRequired,
  quality: PropTypes.object.isRequired,
  runStates: PropTypes.object.isRequired,
  userSub: PropTypes.string,
};

function BrainCount({ workspaceId }) {
  const [count, setCount] = useState(null);
  useEffect(() => {
    let alive = true;
    listBrainFiles(workspaceId).then((files) => { if (alive) { setCount(files.length); } }).catch(() => { if (alive) { setCount(null); } });
    return () => { alive = false; };
  }, [workspaceId]);
  if (count === null) { return null; }
  return <p className="ws-muted">{count === 0 ? 'No notes yet.' : `${count} file${count === 1 ? '' : 's'}.`}</p>;
}

BrainCount.propTypes = { workspaceId: PropTypes.string.isRequired };

/**
 * The workspace's Overview (design "Flow Workspace", Overview): a line of totals and the gadgets (the project Home's
 * machinery: drag, S / M / L, hide, Add gadget, Reset; kept per person under `flow.workspaceGadgets`): Projects, Needs
 * attention, Latest across projects, Members and the workspace Brain. Only what the services answered is shown.
 */
export default function WorkspaceOverview({ workspace, userSub, runStates, quality, activity, onOpenProject, onOpenTab }) {
  const [slot, setSlot] = useState(null);
  const { projects } = workspace;
  const answers = Object.fromEntries(projects.map((project) => [project.projectId, runStates[project.projectId]]));
  const states = projects.map((project) => projectRunState(answers[project.projectId]));
  const attention = attentionItems(projects, answers, quality);
  const blocked = blockedTotal(projects, quality);
  const problems = states.filter((state) => state.key === 'failed' || state.key === 'crashed').length + blocked;
  const running = states.filter((state) => state.key === 'running').length;
  const people = workspace.members.length + 1;

  const events = mergeActivity(Object.fromEntries(Object.entries(activity).map(([id, entry]) => [id, entry?.value])), projects);
  const activityLoading = projects.length > 0 && projects.some((project) => !activity[project.projectId] || activity[project.projectId].status === 'loading');

  const gadgets = {
    projects: <ProjectsGadget activity={activity} onOpenProject={onOpenProject} projects={projects} quality={quality} runStates={answers} userSub={userSub} />,
    attention: (
      <section className="ws-card" data-testid="ws-gadget-attention">
        <h2 className="ws-card-title">Needs attention {attention.length > 0 && <span className="ws-chip is-bad">{attention.length}</span>}</h2>
        {attention.length === 0 ? <p className="ws-muted">Nothing needs you. No failed builds, and no quality check blocks Publish.</p> : (
          <ul className="ws-rows is-plain">
            {attention.map((item) => (
              <li className="ws-attn" key={item.id}>
                <strong className={item.kind === 'build' ? 'ws-bad' : undefined}>{item.title}</strong> {item.text}
                {item.projectId && <button className="ws-link" onClick={() => onOpenProject(item.projectId, { tab: 'logs' })} type="button">Read the log</button>}
                {item.kind === 'quality' && <button className="ws-link" onClick={() => onOpenTab('quality')} type="button">See which</button>}
              </li>
            ))}
          </ul>
        )}
      </section>
    ),
    latest: (
      <section className="ws-card" data-testid="ws-gadget-latest">
        <h2 className="ws-card-title">Latest across projects</h2>
        {events.length === 0 ? <p className="ws-muted">{activityLoading ? 'Loading…' : 'Nothing happened in the last 30 days.'}</p> : (
          <ul aria-label="Latest across projects" className="ws-rows">
            {events.slice(0, LATEST).map((event, index) => (
              // eslint-disable-next-line react/no-array-index-key
              <li className="ws-row" key={event.id || `${event.projectId}-${event.at}-${index}`}>
                <time className="ws-muted ws-time" dateTime={event.at}>{eventTime(event.at, { day: true })}</time>
                <span className="ws-tag" title={event.projectName}>{event.projectName}</span>
                <span className="ws-grow">{eventLine(event) || event.type}</span>
              </li>
            ))}
          </ul>
        )}
        {events.length > 0 && <button className="ws-link ws-link-start" onClick={() => onOpenTab('activity')} type="button">See all activity</button>}
      </section>
    ),
    members: (
      <section className="ws-card" data-testid="ws-gadget-members">
        <h2 className="ws-card-title">Members</h2>
        <ul aria-label="Workspace members" className="ws-rows is-plain">
          <li>{memberName(workspace.owner || {}, workspace, userSub)} <span className="ws-muted">Owner</span></li>
          {workspace.members.map((member) => (
            <li key={member.email} title={member.email}>{memberName(member, workspace, userSub)} <span className="ws-muted">{roleLabel(member.role)}</span></li>
          ))}
        </ul>
        <button className="ws-link ws-link-start" onClick={() => onOpenTab('members')} type="button">Manage members</button>
      </section>
    ),
    brain: (
      <section className="ws-card" data-testid="ws-gadget-brain">
        <h2 className="ws-card-title">Workspace Brain</h2>
        <p className="ws-muted">Shared notes every project&apos;s agent reads: naming rules, brand, how to deploy.</p>
        <BrainCount workspaceId={workspace.id} />
        <button className="ws-btn" onClick={() => onOpenTab('brain')} type="button">Open Brain</button>
      </section>
    ),
  };

  return (
    <div className="ws-overview">
      <div className="ws-summary">
        <p className="ws-muted ws-grow" data-testid="ws-summary">
          {projects.length} project{projects.length === 1 ? '' : 's'}
          {' · '}{running} running
          {problems > 0 && <>{' · '}<strong className="ws-bad">{problems} problem{problems === 1 ? '' : 's'}</strong></>}
          {' · '}{people} member{people === 1 ? '' : 's'}
        </p>
        <div ref={setSlot} />
      </div>
      <HomeGadgets barSlot={slot} buttonClass="ws-btn" gadgets={gadgets} model={workspaceGadgetsModel} primaryClass="is-primary" />
    </div>
  );
}

WorkspaceOverview.propTypes = {
  activity: PropTypes.object.isRequired,
  onOpenProject: PropTypes.func.isRequired,
  onOpenTab: PropTypes.func.isRequired,
  quality: PropTypes.object.isRequired,
  runStates: PropTypes.object.isRequired,
  userSub: PropTypes.string,
  workspace: PropTypes.object.isRequired,
};
